import { useState } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail, // ✅ ADD THIS LINE
} from "firebase/auth";

import { auth } from "../../firebase";
import { deleteProfilePhoto } from "../../utils/indexedDB";

export default function Login({ setSection, sidebarTheme, clearForcedTheme }) {
  const isMobile = window.matchMedia("(max-width: 600px)").matches;

  const s = styles(sidebarTheme, isMobile);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState(""); // ✅ ADD THIS LINE

  /* ===== EMAIL LOGIN ===== */
  async function handleLogin() {
    setError("");
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // 🚫 BLOCK if email not verified
      await cred.user.reload(); // 🔄 always refresh status
      if (!cred.user.emailVerified) {
        await auth.signOut();
        setError("Please verify your email before logging in.");
        setLoading(false); // ✅ ADD THIS LINE
        return;
      }

      localStorage.removeItem("isFirstVisit");
      clearForcedTheme();
      setSection("practice");
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  }
  /* ===== FORGOT PASSWORD ===== */
  async function handleForgotPassword() {
    setError("");
    setResetMsg("");

    if (!email) {
      setError("Please enter your email address first.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg("Password reset link sent. Check your email.");
    } catch (err) {
      setError(err.message);
    }
  }

  /* ===== GOOGLE LOGIN ===== */
  async function handleGoogleLogin() {
    setError("");
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      localStorage.removeItem("isFirstVisit");
      clearForcedTheme();
      setSection("practice");
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  }

  return (
    <div style={s.overlay}>
      <div style={s.page}>
        <h1 style={s.title}>Sign In</h1>

        <p style={s.notice}>
          <strong>Important Notice</strong>
          <br />
          If you continue without signing in, your test history and performance
          analysis will not be recorded or displayed. Certain features will
          remain unavailable until you sign in.
        </p>

        <input
          style={s.input}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          style={s.input}
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="button"
          onClick={handleForgotPassword}
          style={{
            background: "none",
            border: "none",
            color: sidebarTheme.accent,
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            marginBottom: 12,
            textAlign: "right",
            width: "100%",
          }}
        >
          Forgot password?
        </button>

        {error && <div style={s.error}>{error}</div>}

        {resetMsg && (
          <div
            style={{
              color: "#22c55e",
              fontSize: 13,
              marginBottom: 10,
              fontWeight: 600,
            }}
          >
            {resetMsg}
          </div>
        )}

        <button style={s.primaryBtn} onClick={handleLogin} disabled={loading}>
          Sign In
        </button>

        <div style={s.divider}>OR</div>

        <button style={s.googleBtn} onClick={handleGoogleLogin}>
          Continue with Google
        </button>

        <button
          style={s.skipBtn}
          onClick={async () => {
            await deleteProfilePhoto(); // ✅ ADD THIS LINE
            clearForcedTheme();
            setSection("practice");
          }}
        >
          Continue without signing in
        </button>

        <p style={s.terms}>
          By signing in, you accept our{" "}
          <span style={s.link} onClick={() => setSection("terms-auth")}>
            Terms and Conditions
          </span>
        </p>

        <p style={s.switch}>
          New user?{" "}
          <span style={s.link} onClick={() => setSection("register")}>
            Create an account
          </span>
        </p>
      </div>
    </div>
  );
}

/* ===== STYLES (THEME AWARE) ===== */
const styles = (theme, isMobile) => ({
  overlay: {
    position: "fixed",
    inset: 0,

    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(24px)",

    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",

    overflowY: "auto",
    overflowX: "hidden", // 🔥 PREVENT LEFT–RIGHT SCROLL
    padding: "16px 0",
  },

  page: {
    width: "100%",
    maxWidth: 420,
    margin: "0 16px",
    padding: isMobile ? 16 : 24,

    boxSizing: "border-box", // ✅ optional safety

    textAlign: "center",
    background: theme.card,
    borderRadius: isMobile ? 12 : 14,
    border: `1px solid ${theme.border}`,
    color: theme.text,
  },

  title: {
    fontSize: isMobile ? 24 : 32,
    fontWeight: 800,
    marginBottom: isMobile ? 14 : 16,
  },

  notice: {
    fontSize: 14,
    lineHeight: 1.6,
    opacity: 0.85,
    marginBottom: 24,
  },

  input: {
    width: "100%",
    boxSizing: "border-box", // 🔥 THIS IS THE KEY FIX

    padding: isMobile ? "14px 14px" : "12px 14px",
    marginBottom: 12,

    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    background: theme.bgMain,
    color: theme.text,
    outline: "none",
    fontSize: isMobile ? 15 : 14,
  },

  primaryBtn: {
    width: "100%",
    padding: isMobile ? 14 : 12,
    borderRadius: 10,
    border: "none",
    background: theme.accentGradient,
    color: "#fff",
    fontWeight: 600,
    fontSize: isMobile ? 15 : 14,
    cursor: "pointer",
  },

  googleBtn: {
    width: "100%",
    padding: isMobile ? 14 : 12,
    borderRadius: 10,
    border: `1px solid ${theme.border}`,
    background: "transparent",
    color: theme.text,
    fontWeight: 600,
    fontSize: isMobile ? 15 : 14,
    cursor: "pointer",
  },

  skipBtn: {
    marginTop: 14,
    background: "none",
    border: "none",
    color: theme.accent,
    cursor: "pointer",
    fontSize: 14,
  },

  divider: {
    margin: "20px 0",
    opacity: 0.6,
  },

  error: {
    color: theme.danger,
    fontSize: 13,
    marginBottom: 10,
  },

  terms: {
    fontSize: 13,
    marginTop: 14,
    opacity: 0.8,
  },

  switch: {
    marginTop: 18,
    fontSize: 14,
  },

  link: {
    color: theme.accent,
    cursor: "pointer",
    fontWeight: 600,
  },
});
